import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import {
  adaptFiltersFromUrl,
  formatBudget
} from '@spotahome/marketplace-common/src/utils/filters';

import {
  useHotspotItem,
  HOTSPOT_FILTERS
} from '../../../context/HotspotsContext';
import PriceSliderFilter from '../../PriceSliderFilter';
import PriceRangeHelper from '../../PriceRangeHelper';

import DropdownButton from '../../DropdownButton';

import FiltersExposedTitle from './FiltersExposedTitle';
import FiltersExposedButton from './FiltersExposedButton';
import FiltersExposedInfoBox from './FiltersExposedInfoBox';
import BillsFilter from './BillsFilter';
import CheckboxSectionFilter from './CheckboxSectionFilter';

const BudgetFilter = ({
  isFilterOpen,
  selectedBills,
  onToggleFilter,
  currencyIsoCode,
  onFiltersUpdate,
  filters,
  totalResults,
  sortingAlgorithm,
  maxPriceInCity,
  cityId,
  className
}) => {
  const { setHotspotVisited, renderHotspotLed } = useHotspotItem(
    HOTSPOT_FILTERS.budget
  );

  const handleClose = () => {
    setHotspotVisited();
    onToggleFilter('price');
  };

  return (
    <DropdownButton
      className={className}
      isOpen={isFilterOpen}
      isSelected={!!filters.budget || !!selectedBills.length}
      onToggle={() => onToggleFilter('price')}
      onClose={handleClose}
      dataTest="price-exposed-filter-button"
      renderButtonContent={() => (
        <Fragment>
          {renderHotspotLed()}
          {filters.budget
            ? formatBudget(filters.budget, currencyIsoCode)
            : trans('filters.label.budget')}
        </Fragment>
      )}
      renderDropdownContent={handleDropdownClose => (
        <>
          <div className={'filters-exposed__content'}>
            <FiltersExposedTitle
              title={trans('filters.label.budget')}
              subtitle={trans('exposed-filters.dropdown.price.description')}
            />
            <FiltersExposedInfoBox filterName={HOTSPOT_FILTERS.budget} />

            <PriceSliderFilter
              showLabel={false}
              budget={filters.budget}
              currencyIsoCode={currencyIsoCode}
              maxPriceInCity={maxPriceInCity}
              updateFiltersWith={onFiltersUpdate}
              showMinMaxHelpers
              histogramMaxHeight={60}
              adaptedFilters={adaptFiltersFromUrl(filters, sortingAlgorithm)}
              cityId={cityId}
            />

            <PriceRangeHelper
              budget={filters.budget}
              currencyIsoCode={currencyIsoCode}
              updateFiltersWith={onFiltersUpdate}
              maxPriceInCity={maxPriceInCity}
            />

            <CheckboxSectionFilter
              filters={filters}
              onFiltersUpdate={onFiltersUpdate}
              titleKey="search.filter.advanced-search"
            />
            <BillsFilter
              features={filters['features[]']}
              onFiltersUpdate={onFiltersUpdate}
            />
          </div>
          <FiltersExposedButton
            onClick={handleDropdownClose}
            text={trans('search.filters.view_close_filters', {
              nResults: `${totalResults}`
            })}
            dataTest="price-exposed-filter-apply"
            eventLabel="apply-price"
          />
        </>
      )}
    />
  );
};

BudgetFilter.propTypes = {
  isFilterOpen: PropTypes.bool.isRequired,
  selectedBills: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleFilter: PropTypes.func.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  sortingAlgorithm: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  totalResults: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    flexibleMoveIn: PropTypes.number,
    flexibleMoveOut: PropTypes.number,
    budget: PropTypes.string,
    noDeposit: PropTypes.number,
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    'rentalType[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'features[]': PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  maxPriceInCity: PropTypes.number.isRequired,
  cityId: PropTypes.string.isRequired,
  className: PropTypes.string
};

BudgetFilter.defaultProps = {
  className: ''
};

export default BudgetFilter;
